import { Grid } from '@mui/material';
import FieldText from '../../../components/general/fields/fieldText';
import FieldSelect from '../../../components/general/fields/fieldSelect';
import { Checkbox } from '../../../components/general/fields/checkbox';
import { OrbitSectionHeader } from '../components/orbitSectionHeader';
import { useOrbitCitizenInfo } from './hook';
import { OrbitCitizenInfoProps } from '../sectionProps';

export const OrbitCitizenInfo = (props: OrbitCitizenInfoProps) => {
    const { readOnly } = props;
    const {
        formik,
        typesDocument,
        typesPerson,
        genders,
        isJuridical,
        loadingDocumentTypes,
        loadingPersonTypes,
        loadingGenders,
        documentTypesError,
        personTypesError,
        gendersError,
        isLookupDebouncing,
    } = useOrbitCitizenInfo(props);

    const disabled = Boolean(readOnly);
    const { lookingUpAccount } = props;
    const isLookingUpAccount = isLookupDebouncing || Boolean(lookingUpAccount);

    return (
        <Grid
            container
            spacing={{ xs: 1.5, md: 1.5 }}
            columnSpacing={{ xs: 1.5, sm: 1.5, md: 1.5 }}
            pt="20px"
        >
            <Grid item xs={12}>
                <OrbitSectionHeader title="Información del Ciudadano" />
            </Grid>

            <Grid item xs={12} md={4}>
                {readOnly ? (
                    <FieldText
                        label="Tipo de Persona"
                        name="personType"
                        size="small"
                        disabled
                        value={formik.values.personType}
                    />
                ) : (
                    <FieldSelect
                        data={typesPerson}
                        label="Tipo de Persona"
                        name="personType"
                        size="small"
                        required
                        disabled={
                            loadingPersonTypes ||
                            Boolean(personTypesError) ||
                            typesPerson.length === 0
                        }
                        onBlur={formik.handleBlur}
                        touched={
                            formik.touched.personType ||
                            Boolean(personTypesError)
                        }
                        errors={formik.errors.personType || personTypesError}
                        value={formik.values.personType}
                        onChange={formik.handleChange}
                    />
                )}
            </Grid>

            <Grid item xs={12} md={4}>
                {readOnly ? (
                    <FieldText
                        label="Tipo doc."
                        name="typeDocument"
                        size="small"
                        disabled
                        value={formik.values.typeDocument}
                    />
                ) : (
                    <FieldSelect
                        data={typesDocument}
                        label="Tipo doc."
                        name="typeDocument"
                        size="small"
                        required
                        disabled={
                            loadingDocumentTypes ||
                            Boolean(documentTypesError) ||
                            typesDocument.length === 0
                        }
                        onBlur={formik.handleBlur}
                        touched={
                            formik.touched.typeDocument ||
                            Boolean(documentTypesError)
                        }
                        errors={
                            formik.errors.typeDocument || documentTypesError
                        }
                        value={formik.values.typeDocument}
                        onChange={formik.handleChange}
                    />
                )}
            </Grid>

            <Grid item xs={12} md={4}>
                <FieldText
                    label="Número de documento"
                    name="document"
                    placeholder="Número de documento"
                    size="small"
                    required
                    disabled={disabled}
                    loading={!readOnly && isLookingUpAccount}
                    inputProps={{ maxLength: 15, readOnly: disabled }}
                    onBlur={formik.handleBlur}
                    touched={formik.touched.document}
                    errors={formik.errors.document}
                    value={formik.values.document}
                    onChange={formik.handleChange}
                />
            </Grid>

            <Grid item xs={12} md={isJuridical ? 12 : 4}>
                <FieldText
                    label={isJuridical ? 'Nombre empresa' : 'Nombre'}
                    name="name"
                    placeholder={
                        isJuridical ? 'Nombre de la empresa' : 'Nombres'
                    }
                    size="small"
                    required
                    disabled={disabled}
                    inputProps={{
                        maxLength: isJuridical ? 50 : 25,
                        readOnly: disabled,
                    }}
                    onBlur={formik.handleBlur}
                    touched={formik.touched.name}
                    errors={formik.errors.name}
                    value={formik.values.name}
                    onChange={formik.handleChange}
                />
            </Grid>

            {!isJuridical && (
                <Grid item xs={12} md={4}>
                    <FieldText
                        label="Apellidos"
                        name="lastName"
                        placeholder="Apellidos"
                        size="small"
                        required
                        disabled={disabled}
                        inputProps={{ maxLength: 25, readOnly: disabled }}
                        onBlur={formik.handleBlur}
                        touched={formik.touched.lastName}
                        errors={formik.errors.lastName}
                        value={formik.values.lastName}
                        onChange={formik.handleChange}
                    />
                </Grid>
            )}

            <Grid item xs={12} md={4}>
                <FieldText
                    label="Correo Electrónico"
                    name="email"
                    placeholder="correo@ejemplo.com"
                    size="small"
                    required
                    disabled={disabled}
                    inputProps={{ maxLength: 50, readOnly: disabled }}
                    onBlur={formik.handleBlur}
                    touched={formik.touched.email}
                    errors={formik.errors.email}
                    value={formik.values.email}
                    onChange={formik.handleChange}
                />
            </Grid>

            <Grid item xs={12} md={4}>
                <FieldText
                    label="Correo Electrónico Alternativo"
                    name="alternateEmail"
                    placeholder="correo@ejemplo.com"
                    size="small"
                    disabled={disabled}
                    inputProps={{ maxLength: 50, readOnly: disabled }}
                    onBlur={formik.handleBlur}
                    touched={formik.touched.alternateEmail}
                    errors={formik.errors.alternateEmail}
                    value={formik.values.alternateEmail}
                    onChange={formik.handleChange}
                />
            </Grid>

            <Grid item xs={12} md={4}>
                <FieldText
                    label="Celular"
                    name="cellPhone"
                    placeholder="+573001234567"
                    size="small"
                    disabled={disabled}
                    inputProps={{ maxLength: 15, readOnly: disabled }}
                    onBlur={formik.handleBlur}
                    touched={formik.touched.cellPhone}
                    errors={formik.errors.cellPhone}
                    value={formik.values.cellPhone}
                    onChange={formik.handleChange}
                />
            </Grid>

            <Grid item xs={12} md={4}>
                <FieldText
                    label="Teléfono Fijo"
                    name="phone"
                    placeholder="(601)1234567"
                    size="small"
                    disabled={disabled}
                    inputProps={{ maxLength: 15, readOnly: disabled }}
                    onBlur={formik.handleBlur}
                    touched={formik.touched.phone}
                    errors={formik.errors.phone}
                    value={formik.values.phone}
                    onChange={formik.handleChange}
                />
            </Grid>

            <Grid item xs={12} md={4}>
                {readOnly ? (
                    <FieldText
                        label="Género"
                        name="gender"
                        size="small"
                        disabled
                        value={formik.values.gender}
                    />
                ) : (
                    <FieldSelect
                        data={genders}
                        label="Género"
                        name="gender"
                        size="small"
                        disabled={
                            isJuridical ||
                            loadingGenders ||
                            Boolean(gendersError) ||
                            genders.length === 0
                        }
                        onBlur={formik.handleBlur}
                        touched={formik.touched.gender || Boolean(gendersError)}
                        errors={formik.errors.gender || gendersError}
                        value={formik.values.gender}
                        onChange={isJuridical ? undefined : formik.handleChange}
                    />
                )}
            </Grid>

            {!isJuridical && (
                <Grid item xs={12} md={4}>
                    {readOnly ? (
                        <FieldText
                            label="¿Se identifica como LGBTIQ+?"
                            name="isLGBTIQ"
                            size="small"
                            disabled
                            value={formik.values.isLGBTIQ ? 'Sí' : 'No'}
                        />
                    ) : (
                        <Checkbox
                            isCheckMode={false}
                            label="¿Se identifica como LGBTIQ+?"
                            data={[
                                { id: 'true', name: 'Sí' },
                                { id: 'false', name: 'No' },
                            ]}
                            checked={Boolean(formik.values.isLGBTIQ)}
                            onChange={(newValue) =>
                                formik.setFieldValue('isLGBTIQ', newValue)
                            }
                            onBlur={formik.handleBlur}
                        />
                    )}
                </Grid>
            )}
        </Grid>
    );
};
